import { MouseEvent, useEffect, useMemo } from "react";
import {
  Control,
  FieldValues,
  UseFormRegister,
  UseFormResetField,
  useWatch,
} from "react-hook-form";
import { normalizeDuplicateName } from "tng-address-collection-shared/normalizeDuplicateName.ts";
import {
  CardsListItem,
  CreateCardRequest,
  EditCardRequest,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { CardFields } from "./CardFields.tsx";
import { RecipientFields } from "./RecipientFields.tsx";

export type CardNameRef = Pick<CardsListItem, "id" | "firstName" | "lastName">;

export type EmailDuplicateState =
  | { status: "checking" }
  | { status: "unique" }
  | { status: "duplicate-blocked" }
  | {
      status: "duplicate-shareable";
      isSharing: boolean;
      onShare: (event?: MouseEvent) => void;
    };

interface CardFormFieldsProps<T extends FieldValues> {
  control: Control<T>;
  resetField: UseFormResetField<T>;
  register: UseFormRegister<T>;
  projectId: string;
  cards: CardNameRef[];
  currCardId?: string;
  emailDuplicateState: EmailDuplicateState;
  responsibleProjectId?: string;
}

export const CardFormFields = ({
  control,
  resetField,
  register,
  projectId,
  cards,
  currCardId,
  emailDuplicateState,
  responsibleProjectId,
}: CardFormFieldsProps<CreateCardRequest | EditCardRequest>) => {
  const isFieldsDisabled =
    emailDuplicateState.status === "duplicate-blocked" ||
    emailDuplicateState.status === "duplicate-shareable";

  const currentPreferredLanguage = useWatch({
    name: "recipient.preferredLanguage",
    control,
  });
  const currentFormality = useWatch({
    name: "formality",
    control,
  });

  useEffect(() => {
    if (
      currentFormality === "semi-formal" &&
      currentPreferredLanguage === "en"
    ) {
      resetField("formality");
    }
  }, [currentPreferredLanguage, currentFormality, resetField]);

  const currentFirstName = useWatch({
    name: "recipient.firstName",
    control,
  });

  const currentLastName = useWatch({
    name: "recipient.lastName",
    control,
  });

  const calculateNameDuplicate = useMemo(() => {
    const names = new Set(
      cards
        .filter((card) => !currCardId || card.id !== currCardId)
        .map((card) => normalizeDuplicateName(card.firstName, card.lastName)),
    );

    return (firstName: string, lastName: string) =>
      names.has(normalizeDuplicateName(firstName, lastName));
  }, [cards, currCardId]);

  const isNameDuplicate = useMemo(
    () => calculateNameDuplicate(currentFirstName, currentLastName),
    [calculateNameDuplicate, currentFirstName, currentLastName],
  );

  return (
    <>
      <RecipientFields
        isNameDuplicate={isNameDuplicate}
        control={control}
        isFieldsDisabled={isFieldsDisabled}
        register={register}
        emailDuplicateState={emailDuplicateState}
        currentPreferredLanguage={currentPreferredLanguage}
        projectId={projectId}
        responsibleProjectId={responsibleProjectId}
      />

      {emailDuplicateState.status !== "duplicate-shareable" && (
        <CardFields control={control} isFieldsDisabled={isFieldsDisabled} />
      )}
    </>
  );
};
